import dayjs from 'dayjs';
import { DataModel } from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { Discount } from './discount.js';
import { EntityMixin } from '../../../components/core/entity-mixin.js';
import { Result } from '../../../utility/result.js';
import { ErrorLike } from '../../core/error-like.js';
import { IProductWithSerialNumber } from '../../../contracts/product-with-serial-number.js';
import { DiscountCodeBase } from '../../../contracts/discount-code.js';

export enum VoucherStatus {
  Live = 'LIVE',
  Used = 'USED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
  Archived = 'ARCHIVED',
}

export abstract class Voucher extends EntityMixin(DataModel) implements DiscountCodeBase {
  abstract get uniqueId(): string;
  abstract get code(): string;
  abstract get businessRef(): BusinessModuleRef;
  abstract get creationDate(): dayjs.Dayjs;
  abstract get status(): VoucherStatus;
  abstract get usedAt(): dayjs.Dayjs | null;
  abstract get startDate(): dayjs.Dayjs | null;
  abstract get expirationDate(): dayjs.Dayjs | null;

  abstract updateUsage(discount: Discount, now?: dayjs.Dayjs): void;
  abstract tryUpdateUsage(discount: Discount, now: dayjs.Dayjs): Result<this, ErrorLike>;

  // EXTENSION METHODS
  static encodedStatusSafe(status: VoucherStatus): string {
    return fromEnumAsString(status);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof Voucher;
  }

  static assertType(dm: DataModel): dm is Voucher {
    return dm instanceof Voucher;
  }
}

export abstract class PromoCode extends Voucher {
  abstract get maxUses(): number | null;
  abstract get uses(): number | null;

  static verifyType(dm: DataModel): boolean {
    return dm instanceof PromoCode;
  }

  static assertType(dm: DataModel): dm is PromoCode {
    return dm instanceof PromoCode;
  }
}

export abstract class SellableVoucher extends Voucher implements IProductWithSerialNumber {
  abstract get orderId(): string;
  abstract get userId(): string | null;
  abstract get isGuestOrder(): boolean | null;
  abstract get orderEntryId(): string | null;
  abstract get serialNumber(): string | null;

  abstract setSerialNumber(serialNumber: string): this;
  abstract copyAsCanceled(isRefund: boolean): this;

  static verifyType(dm: DataModel): boolean {
    return dm instanceof SellableVoucher;
  }

  static assertType(dm: DataModel): dm is SellableVoucher {
    return dm instanceof SellableVoucher;
  }
}
